import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelAiCampaign,
  createAiCampaign,
  createAiCampaignDrafts,
  getAiCampaignDeliveries,
  listAiCampaigns,
  pauseAiCampaign,
  prepareAiCampaign,
  previewAiCampaignRecipients,
  startAiCampaign,
  updateAiCampaign,
  type AiCampaignCreateRequestDto,
  type AiCampaignUpdateRequestDto,
  type CreateAiCampaignDraftsRequestDto,
  type AiCampaignRecipientPreviewRequestDto,
} from '@/shared/api/ai-campaigns';

export const campaignQueryKeys = {
  list: (companyId: string | undefined) => ['ai-campaigns', companyId],
  deliveries: (campaignId: string | undefined) => [
    'ai-campaign-deliveries',
    campaignId,
  ],
} as const;

export function useCampaigns(companyId: string | undefined) {
  return useQuery({
    queryKey: campaignQueryKeys.list(companyId),
    queryFn: () => listAiCampaigns(companyId!),
    enabled: Boolean(companyId),
  });
}

export function useCampaignDeliveries(campaignId: string | undefined) {
  return useQuery({
    queryKey: campaignQueryKeys.deliveries(campaignId),
    queryFn: () => getAiCampaignDeliveries(campaignId!),
    enabled: Boolean(campaignId),
  });
}

export function useCampaignMutations(companyId: string | undefined) {
  const queryClient = useQueryClient();

  const invalidateCampaigns = () => {
    if (!companyId) return;
    void queryClient.invalidateQueries({
      queryKey: campaignQueryKeys.list(companyId),
    });
  };

  const invalidateDeliveries = (campaignId: string | undefined) => {
    if (!campaignId) return;
    void queryClient.invalidateQueries({
      queryKey: campaignQueryKeys.deliveries(campaignId),
    });
  };

  return {
    create: useMutation({
      mutationFn: (data: AiCampaignCreateRequestDto) => createAiCampaign(data),
      onSuccess: invalidateCampaigns,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        data,
      }: {
        id: string;
        data: AiCampaignUpdateRequestDto;
      }) => updateAiCampaign(id, data),
      onSuccess: (campaign) => {
        invalidateCampaigns();
        invalidateDeliveries(campaign.id);
      },
    }),
    previewRecipients: useMutation({
      mutationFn: (data: AiCampaignRecipientPreviewRequestDto) =>
        previewAiCampaignRecipients(data),
    }),
    generateDrafts: useMutation({
      mutationFn: (data: CreateAiCampaignDraftsRequestDto) =>
        createAiCampaignDrafts(data),
    }),
    prepare: useMutation({
      mutationFn: (id: string) => prepareAiCampaign(id),
      onSuccess: (campaign) => {
        invalidateCampaigns();
        invalidateDeliveries(campaign.id);
      },
    }),
    start: useMutation({
      mutationFn: (id: string) => startAiCampaign(id),
      onSuccess: (campaign) => {
        invalidateCampaigns();
        invalidateDeliveries(campaign.id);
      },
    }),
    pause: useMutation({
      mutationFn: (id: string) => pauseAiCampaign(id),
      onSuccess: (campaign) => {
        invalidateCampaigns();
        invalidateDeliveries(campaign.id);
      },
    }),
    cancel: useMutation({
      mutationFn: (id: string) => cancelAiCampaign(id),
      onSuccess: (campaign) => {
        invalidateCampaigns();
        invalidateDeliveries(campaign.id);
      },
    }),
  };
}
